import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { apiFetch } from "../lib/api.js";
import { PageLayout } from "../components/PageLayout.js";
import { ErrorState, LoadingState } from "../components/States.js";

type Tenant = {
  id: string;
  name: string;
  slug: string;
  auth_mode: string;
  created_at: string;
};

type SortOption = "created_desc" | "created_asc" | "name_asc" | "name_desc" | "slug_asc" | "slug_desc";

function slugify(input: string) {
  return input
    .toLowerCase()
    .trim()
    .replace(/['"]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64);
}

function fmt(ts: string) {
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return ts;
  return d.toLocaleDateString();
}

export function Tenants() {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [authMode, setAuthMode] = useState<"atlas_local" | "oidc" | "saml">("atlas_local");
  const [initialAdminEmail, setInitialAdminEmail] = useState("");

  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const lastRefreshAtRef = useRef<number>(0);

  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<SortOption>("created_desc");
  const createRef = useRef<HTMLDivElement | null>(null);

  async function refresh() {
    const now = Date.now();
    // Throttle: avoid hammering the API when focus bounces.
    if (now - lastRefreshAtRef.current < 15_000) return;
    lastRefreshAtRef.current = now;

    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch<{ tenants: Tenant[] }>("/api/admin/tenants");
      setTenants(data.tenants);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    // initial load shouldn't be throttled
    lastRefreshAtRef.current = 0;
    void refresh();
  }, []);

  useEffect(() => {
    function onFocus() {
      void refresh();
    }
    function onVisibility() {
      if (document.visibilityState === "visible") void refresh();
    }
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (slugTouched) return;
    if (name.trim() === "") return;
    setSlug(slugify(name));
  }, [name, slugTouched]);

  async function createTenant() {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch<{ tenant: Tenant; invite_email_sent?: boolean }>("/api/admin/tenants", {
        method: "POST",
        body: { name, slug, auth_mode: authMode, initial_admin_email: initialAdminEmail }
      });
      setTenants((prev) => [data.tenant, ...prev]);
      setName("");
      setSlug("");
      setSlugTouched(false);
      setInitialAdminEmail("");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  const q = query.trim().toLowerCase();
  const filtered = tenants.filter((t) => {
    if (!q) return true;
    return (
      t.name.toLowerCase().includes(q) ||
      t.slug.toLowerCase().includes(q) ||
      t.id.toLowerCase().includes(q) ||
      t.auth_mode.toLowerCase().includes(q)
    );
  });

  const sorted = [...filtered].sort((a, b) => {
    switch (sort) {
      case "created_asc":
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      case "created_desc":
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      case "name_desc":
        return b.name.localeCompare(a.name);
      case "slug_asc":
        return a.slug.localeCompare(b.slug);
      case "slug_desc":
        return b.slug.localeCompare(a.slug);
      case "name_asc":
      default:
        return a.name.localeCompare(b.name);
    }
  });

  return (
    <div>
      <PageLayout
        title="Tenants"
        description={undefined}
      >
        {error && <ErrorState detail={error} />}
        {loading && <LoadingState />}

        <div className="card" style={{ marginTop: 12 }}>
          <div className="toolbar">
            <div className="toolbarGroup" style={{ flex: "1 1 380px" }}>
              <input
                className="input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search tenants (name, slug, id, auth mode)…"
                aria-label="Search tenants"
              />
            </div>
            <div className="toolbarGroup">
              <label className="srOnly" htmlFor="tenantSort">
                Sort
              </label>
              <select id="tenantSort" className="select" value={sort} onChange={(e) => setSort(e.target.value as SortOption)}>
                <option value="created_desc">Created (newest)</option>
                <option value="created_asc">Created (oldest)</option>
                <option value="name_asc">Name (A–Z)</option>
                <option value="name_desc">Name (Z–A)</option>
                <option value="slug_asc">Slug (A–Z)</option>
                <option value="slug_desc">Slug (Z–A)</option>
              </select>
            </div>
          </div>

          <div className="help" style={{ marginTop: 8 }}>
            Showing <b>{sorted.length}</b> of <b>{tenants.length}</b>.
          </div>
        </div>

        <div className="card tableWrap" style={{ marginTop: 12 }}>
          <table className="table">
            <thead>
              <tr>
                <th>Tenant</th>
                <th>Slug</th>
                <th>Auth</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((t) => (
                <tr key={t.id}>
                  <td>
                    <div style={{ fontWeight: 800 }}>
                      <Link to={`/admin/tenants/${t.id}`}>{t.name}</Link>
                    </div>
                    <div className="muted mono" style={{ fontSize: 12 }}>
                      {t.id}
                    </div>
                  </td>
                  <td className="muted">{t.slug}</td>
                  <td>
                    <span className="pill">{t.auth_mode}</span>
                  </td>
                  <td className="muted">{fmt(t.created_at)}</td>
                </tr>
              ))}
              {!loading && !error && sorted.length === 0 && (
                <tr>
                  <td colSpan={4} className="muted" style={{ padding: 12 }}>
                    No tenants match your search.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="card" style={{ marginTop: 12 }} ref={createRef}>
          <div style={{ fontWeight: 800, marginBottom: 6 }}>Create tenant</div>
          <div className="help" style={{ marginBottom: 12 }}>
            Creates the tenant and sends an invite email to the initial admin.
          </div>

          <div style={{ display: "grid", gap: 10, marginBottom: 12 }}>
            <label className="field">
              <span className="label">Name</span>
              <input
                className="input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Acme Manufacturing"
                autoComplete="organization"
              />
            </label>

            <label className="field">
              <span className="label">Slug</span>
              <input
                className="input"
                value={slug}
                onChange={(e) => {
                  setSlugTouched(true);
                  setSlug(e.target.value);
                }}
                placeholder="acme-mfg"
                inputMode="url"
              />
              <div className="help">Used as a stable identifier across systems. Lowercase, numbers, hyphens.</div>
            </label>

            <label className="field">
              <span className="label">Auth mode</span>
              <select className="select" value={authMode} onChange={(e) => setAuthMode(e.target.value as typeof authMode)}>
                <option value="atlas_local">atlas_local</option>
                <option value="oidc">oidc</option>
                <option value="saml">saml</option>
              </select>
            </label>

            <label className="field">
              <span className="label">Initial tenant admin email</span>
              <input
                className="input"
                value={initialAdminEmail}
                onChange={(e) => setInitialAdminEmail(e.target.value)}
                placeholder="admin@customer.com"
                autoComplete="email"
              />
            </label>
          </div>

          <div className="actions">
            <button
              onClick={() => {
                setName("");
                setSlug("");
                setSlugTouched(false);
                setAuthMode("atlas_local");
                setInitialAdminEmail("");
              }}
              disabled={loading}
              className="btn"
              type="button"
            >
              Clear
            </button>
          <button
            onClick={() => void createTenant()}
            disabled={loading || name.trim() === "" || slug.trim() === "" || initialAdminEmail.trim() === ""}
            className="btn btnPrimary"
              type="button"
          >
              Create tenant
          </button>
          </div>
        </div>
      </PageLayout>
    </div>
  );
}

